import { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";

import { UtagData } from "./useExternalScript";

import { fetchSurveyData, setModal } from "state/config-slice";
import { AppDispatch } from "state/reducers-combiner";
import { MedalliaData } from "types/MedalliaData";
import Offer from "types/Offer";

interface User {
  loggedIn: boolean;
  mustSelectContract?: boolean;
}

interface UseSurveyConsentHandlerProps {
  survey: MedalliaData;
  isSdkLoaded: boolean;
  user: User;
  activeOffers: Offer[];
  consentUnknown: boolean;
  consentGranted: boolean;
  surveyLoading: boolean;
  loadMedalliaForm: (formId: string) => void;
  removeScriptAndCookies: () => void;
  loadScript: (sdkUrl: string, utagData: UtagData) => void;
  utagData: UtagData;
}

export function useSurveyConsentHandler({
  survey,
  isSdkLoaded,
  user,
  activeOffers,
  consentUnknown,
  consentGranted,
  surveyLoading,
  loadMedalliaForm,
  removeScriptAndCookies,
  loadScript,
  utagData,
}: UseSurveyConsentHandlerProps): void {
  const dispatch = useDispatch<AppDispatch>();
  const isLoggedIn = Boolean(user?.loggedIn);
  const hasContractSelected = !user?.mustSelectContract;

  const surveyConfig = useMemo(() => survey?.config, [survey]);
  const activationForm = useMemo(() => surveyConfig?.forms?.find((form) => form.type === "activation"), [surveyConfig]);

  const formId = useMemo(() => activationForm?.id, [activationForm]);
  const sdkUrl = useMemo(() => surveyConfig?.sdkUrl, [surveyConfig]);
  const activationEnabled = useMemo(
    () => activationForm?.enabledForMarket ?? surveyConfig?.enabledForMarket ?? false,
    [activationForm, surveyConfig],
  );

  const anySurveyEnabled = useMemo(() => {
    if (surveyConfig?.forms?.some((form) => form.enabledForMarket)) return true;
    return surveyConfig?.enabledForMarket ?? false;
  }, [surveyConfig]);

  const hasSurveyConfig = useMemo(() => Boolean(surveyConfig?.sdkUrl), [surveyConfig]);
  const hasSurveyHistory = useMemo(() => {
    if (Array.isArray(survey?.history)) return true;
    if (survey?.history && typeof survey.history === "object") return true;
    if (Array.isArray((survey as unknown as { history?: { entries?: unknown[] } })?.history?.entries)) return true;
    return false;
  }, [survey]);

  const didRequestSurvey = useRef(false);
  // Cooldown is based on the latest activation entry regardless of whether it was completed or skipped
  const latestActivationSurveyDate = useMemo(() => {
    let rawHistory: unknown[] = [];

    if (Array.isArray(survey?.history)) {
      rawHistory = survey.history;
    } else if (survey?.history && typeof survey.history === "object") {
      rawHistory = [survey.history];
    } else if (Array.isArray((survey as unknown as { history?: { entries?: unknown[] } })?.history?.entries)) {
      rawHistory = (survey as unknown as { history: { entries: unknown[] } }).history.entries;
    }

    const history = rawHistory as Array<{
      surveyType?: string;
      action?: string;
      date?: string;
      medallia?: { timestamp?: string };
    }>;

    return history.reduce<Date | null>((latestDate, entry) => {
      const surveyType = String(entry?.surveyType || "").toLowerCase();
      if (surveyType !== "activation") return latestDate;
      const entryDateValue = entry?.date || entry?.medallia?.timestamp;
      if (!entryDateValue) return latestDate;
      const entryDate = new Date(entryDateValue);
      if (Number.isNaN(entryDate.getTime())) return latestDate;
      if (!latestDate || entryDate > latestDate) return entryDate;

      return latestDate;
    }, null);
  }, [survey]);

  const hasEligibleActivationOffer = useMemo(() => {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const isOutsideSurveyCooldown = !latestActivationSurveyDate || latestActivationSurveyDate <= sixMonthsAgo;

    if (!isOutsideSurveyCooldown) return false;

    return activeOffers.some((offer) => {
      const activationDateValue = offer?.startDate;
      if (!activationDateValue) return false;
      const activationDate = new Date(activationDateValue);
      if (Number.isNaN(activationDate.getTime())) return false;
      if (activationDate < sixMonthsAgo) return false;
      if (latestActivationSurveyDate && activationDate <= latestActivationSurveyDate) return false;
      return true;
    });
  }, [activeOffers, latestActivationSurveyDate]);

  useEffect(() => {
    const isLandingPage = window.location.pathname === "/landingPage";
    if (!consentGranted) {
      didRequestSurvey.current = false;
    }

    if (
      isLoggedIn &&
      hasContractSelected &&
      consentGranted &&
      (!hasSurveyConfig || !hasSurveyHistory) &&
      !didRequestSurvey.current &&
      !surveyLoading &&
      !isLandingPage
    ) {
      didRequestSurvey.current = true;
      dispatch(fetchSurveyData());
    }
  }, [consentGranted, dispatch, hasSurveyConfig, hasSurveyHistory, isLoggedIn, hasContractSelected, surveyLoading]);

  useEffect(() => {
    if (isLoggedIn && hasContractSelected && consentGranted && anySurveyEnabled && sdkUrl && !isSdkLoaded) {
      console.log("Loading survey utagData script:", utagData);
      loadScript(sdkUrl, utagData);
    }
  }, [anySurveyEnabled, consentGranted, isLoggedIn, hasContractSelected, isSdkLoaded, loadScript, sdkUrl, utagData]);

  useEffect(() => {
    const canShowActivationSurvey =
      isLoggedIn && consentGranted && hasEligibleActivationOffer && activationEnabled && hasSurveyHistory;

    if (canShowActivationSurvey) {
      formId && isSdkLoaded && loadMedalliaForm(formId);
    } else {
      if (!isLoggedIn || !consentGranted || !anySurveyEnabled) {
        removeScriptAndCookies();
      }
    }

    if (isLoggedIn && consentUnknown) {
      dispatch(setModal({ showModal: true, cookieModal: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activationEnabled,
    anySurveyEnabled,
    consentGranted,
    consentUnknown,
    dispatch,
    formId,
    hasEligibleActivationOffer,
    hasSurveyHistory,
    isLoggedIn,
    isSdkLoaded,
    loadMedalliaForm,
    removeScriptAndCookies,
  ]);
}
